import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Video, Trash2, Plus, Loader2, UploadCloud, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';

/**
 * Admin · Tutorials (Session 36, P3 item h).
 * Upload tutorial videos (S3 via /api/upload-video) and tag each to a hub area.
 */
export default function AdminHelpVideosTab() {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [videos, setVideos] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ area_key: '', title: '', description: '', video_url: '', order: 0, active: true });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/help-videos`, { headers });
      setVideos(res.data.videos || []);
      setAreas(res.data.areas || []);
    } catch (err) {
      toast.error('Failed to load tutorials');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/upload-video`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      setForm((f) => ({ ...f, video_url: res.data.file_url }));
      toast.success('Video uploaded');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const resetForm = () => {
    setForm({ area_key: '', title: '', description: '', video_url: '', order: 0, active: true });
    setShowForm(false);
  };

  const save = async () => {
    if (!form.area_key || !form.title || !form.video_url) {
      toast.error('Area, title, and an uploaded video are required');
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/help-videos`, form, { headers });
      toast.success('Tutorial added');
      resetForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (v) => {
    try {
      await axios.patch(`${API}/admin/help-videos/${v.id}`, { active: !v.active }, { headers });
      load();
    } catch (err) {
      toast.error('Update failed');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this tutorial video?')) return;
    try {
      await axios.delete(`${API}/admin/help-videos/${id}`, { headers });
      toast.success('Deleted');
      load();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  if (loading) {
    return <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-teal-400" /></div>;
  }

  return (
    <div className="space-y-5" data-testid="admin-help-videos-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-100 flex items-center gap-2">
            <Video className="w-5 h-5 text-cyan-300" /> Tutorials
          </h3>
          <p className="text-sm text-gray-400">Short videos that teach new users how to navigate each Hub area.</p>
        </div>
        {!showForm && (
          <Button onClick={() => setShowForm(true)} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="add-tutorial-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Tutorial
          </Button>
        )}
      </div>

      {showForm && (
        <Card className="bg-white/5 border-teal-500/30">
          <CardContent className="pt-5 space-y-4">
            <div className="flex justify-between items-center">
              <h4 className="text-sm font-semibold text-gray-200">New Tutorial</h4>
              <button onClick={resetForm} className="text-gray-400 hover:text-gray-200"><X className="w-4 h-4" /></button>
            </div>
            <div className="grid md:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-400">Hub Area</Label>
                <Select value={form.area_key} onValueChange={(v) => setForm({ ...form, area_key: v })}>
                  <SelectTrigger className="bg-white/5 border-white/10 mt-1" data-testid="tutorial-area-select"><SelectValue placeholder="Select area" /></SelectTrigger>
                  <SelectContent>
                    {areas.map((a) => <SelectItem key={a.key} value={a.key}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-gray-400">Display order</Label>
                <Input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })} className="bg-white/5 border-white/10 mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-gray-400">Title</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-white/5 border-white/10 mt-1" data-testid="tutorial-title-input" />
            </div>
            <div>
              <Label className="text-xs text-gray-400">Description (optional)</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-white/5 border-white/10 mt-1" rows={2} />
            </div>
            <div>
              <Label className="text-xs text-gray-400">Video</Label>
              <div className="mt-1 flex items-center gap-3">
                <label className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-white/10 cursor-pointer hover:bg-white/10 text-sm text-gray-200" data-testid="tutorial-upload-label">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <UploadCloud className="w-4 h-4" />}
                  {uploading ? 'Uploading…' : 'Upload video'}
                  <input type="file" accept="video/mp4,video/quicktime,video/webm,video/x-msvideo" className="hidden" onChange={handleUpload} disabled={uploading} data-testid="tutorial-video-input" />
                </label>
                {form.video_url && <span className="text-xs text-emerald-300">✓ Video attached</span>}
              </div>
            </div>
            <Button onClick={save} disabled={saving || uploading} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="save-tutorial-btn">
              {saving ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null} Save Tutorial
            </Button>
          </CardContent>
        </Card>
      )}

      {videos.length === 0 ? (
        <Card className="bg-white/5 border-white/10"><CardContent className="py-10 text-center text-gray-500">
          <Video className="w-8 h-8 mx-auto mb-2 opacity-30" /> No tutorials yet. Add one to guide new users.
        </CardContent></Card>
      ) : (
        <div className="space-y-2">
          {videos.map((v) => (
            <Card key={v.id} className="bg-white/5 border-white/10" data-testid={`tutorial-row-${v.id}`}>
              <CardContent className="py-3 flex items-center gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300">{v.area_label}</span>
                    {!v.active && <span className="text-xs px-2 py-0.5 rounded-full bg-gray-500/20 text-gray-400">Hidden</span>}
                  </div>
                  <p className="text-gray-100 font-medium mt-1 truncate">{v.title}</p>
                  {v.description && <p className="text-xs text-gray-500 truncate">{v.description}</p>}
                </div>
                <div className="flex items-center gap-3">
                  <Switch checked={v.active} onCheckedChange={() => toggleActive(v)} data-testid={`tutorial-toggle-${v.id}`} />
                  <button onClick={() => remove(v.id)} className="text-red-400 hover:text-red-300" data-testid={`tutorial-delete-${v.id}`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
